<template>
  <div style="min-height: 200px">
    <div v-if="apiType !== 'credit-notes'" class="mb-8 grid gap-8 lg:grid-cols-2">
      <div>
        <label class="font-bold">Filtrer sur status production</label>
        <Multiselect
          mode="tags"
          placeholder="Tout montrer"
          noOptionsText="Tout est séléctioner"
          noResultsText="Tout est séléctioner"
          v-model="filters.status_production"
          :options="Object.entries(PRODUCTION_STATUS).map(([value, label]) => ({ value, label }))"
        />
      </div>
      <div v-if="apiType === 'production'">
        <label class="font-bold">Filtrer sur étape production</label>
        <Multiselect
          mode="tags"
          placeholder="Tout montrer"
          noOptionsText="Tout est séléctioner"
          noResultsText="Tout est séléctioner"
          v-model="filters.stage_production"
          :options="Object.entries(PRODUCTION_STAGES).map(([value, label]) => ({ value, label }))"
        />
      </div>
      <div v-if="apiType === 'invoices'">
        <label class="font-bold">Filtrer sur status payments</label>
        <Multiselect
          mode="tags"
          placeholder="Tout montrer"
          noOptionsText="Tout est séléctioner"
          noResultsText="Tout est séléctioner"
          v-model="filters.status_payments"
          :options="Object.entries(PAYMENT_STATUS).map(([value, label]) => ({ value, label }))"
        />
      </div>
    </div>
    <TableProduction :count="count" v-model:items="items" v-if="apiType === 'production'"> </TableProduction>
    <div v-else class="-my-2 sm:-mx-6 lg:-mx-8">
      <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
        <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50" v-if="count > 0">
              <tr>
                <th
                  v-for="(col, index) in columns"
                  :key="col + index"
                  :class="{
                    'text-left': col !== 'Status',
                    'text-right': col === 'Status',
                  }"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider whitespace-nowrap text-uppercase"
                >
                  {{ col }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="count < 1">
                <td
                  :colspan="columns.length"
                  class="bg-white px-6 py-4 whitespace-nowrap text-sm text-center font-medium text-gray-500"
                >
                  <i class="fas fa-folder-open mr-1"></i>
                  Aucun document trouver
                </td>
              </tr>
              <tr
                v-for="(item, index) in items"
                :key="`${item.id}-${index}`"
                class="bg-white"
                :class="{ 'bg-gray-50': index % 2 }"
              >
                <td style="width: 150px" class="px-6 py-4 whitespace-nowrap text-sm">
                  <p class="block font-medium text-gray-900">{{ numberPrefix }}-{{ item.doc_number }}</p>
                  <p class="block font-medium text-gray-500">{{ item.date.split('T')[0] }}</p>
                  <router-link
                    v-if="apiType === 'invoices' && !!item.doc_number_linked"
                    target="_blank"
                    :to="`/quotes/${item.doc_number_linked}`"
                    class="ml-auto block text-gray-600 hover:text-gray-800 cursor-pointer font-base pl-2"
                  >
                    Devis: DV-{{ item.doc_number_linked }}
                  </router-link>
                  <template v-if="apiType === 'quotes' && !!item.linked_invoices">
                    <router-link
                      v-for="invoice_number in item.linked_invoices.split(',')"
                      :key="invoice_number"
                      target="_blank"
                      :to="`/invoices/${invoice_number}`"
                      class="ml-auto block text-gray-600 hover:text-gray-800 cursor-pointer font-base pl-2"
                    >
                      Facture: FA-{{ invoice_number }}
                    </router-link>
                  </template>
                </td>
                <td style="width: 200px" class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  <p class="block">{{ item.price_excl }} HTVA</p>
                  <p class="block">{{ item.price_incl }} TVAC</p>
                </td>
                <td class="px-6 py-4 text-sm" style="max-width: 400px">
                  <router-link :to="`/clients/${item.client_id}`" class="text-gray-600 hover:text-indigo-400">
                    <p class="block font-bold">{{ item.name }}</p>
                    <p class="block">{{ item.vat }}</p>
                  </router-link>
                </td>
                <td class="px-6 py-4 text-sm text-gray-600" style="max-width: 400px" v-if="apiType !== 'credit-notes'">
                  <router-link
                    v-if="['invoices', 'quotes'].includes(apiType)"
                    :to="
                      apiType === 'invoices' && item.doc_number_linked
                        ? `/quotes/${item.doc_number_linked}/upload`
                        : `/${apiType}/${item.doc_number}/upload`
                    "
                    class="inline-block mr-4 text-lg text-blue-600"
                  >
                    <i class="fa fas fa-camera"></i>
                  </router-link>
                  <button
                    v-if="!item.production_id"
                    class="bg-indigo-500 text-white px-2 py-1 rounded border-1 border-blue-300"
                    @click="createProduction(item)"
                  >
                    <i class="fas fa-card"></i>
                    Metre en production
                  </button>
                  <router-link :to="`/production/${item.production_id}`" target="_blank">
                    <ProductionStatus no-change :status="item.status" :id="item.production_id" />
                  </router-link>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right" v-if="paymentStatus">
                  <div class="-mt-1">
                    <span
                      v-if="item.amount_open == item.price_incl"
                      class="bg-red-500 text-white font-bold rounded-full px-2 py-1 text-xs"
                    >
                      Aucun payment
                    </span>
                    <span
                      v-else-if="item.amount_open > 0"
                      class="bg-orange-500 text-white font-bold rounded-full px-2 py-1 text-xs"
                    >
                      Montant restant
                    </span>
                    <span v-else class="bg-green-500 text-white font-bold rounded-full px-2"> Payée </span>
                  </div>
                  <p class="block mt-2" v-if="item.amount_open > 0">{{ item.amount_open }} TVAC</p>
                </td>
                <td style="max-width: 160px" class="pr-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    v-if="addPayment && item.amount_open > 0"
                    @click="$emit('pay', item)"
                    class="ml-auto block text-indigo-600 hover:text-indigo-900 mb-1 -mt-1 cursor-pointer"
                  >
                    + Payment
                  </button>
                  <div>
                    <router-link
                      v-if="apiType === 'quotes' || apiType === 'invoices'"
                      :to="`/${apiType}/${item.doc_number}/edit`"
                      class="ml-auto inline-block text-indigo-600 hover:text-indigo-900 cursor-pointer mb-1 mr-5"
                    >
                      Modifier
                    </router-link>
                    <router-link
                      target="_blank"
                      :to="`/${apiType}/${item.doc_number}`"
                      class="ml-auto inline-block text-indigo-600 hover:text-indigo-900 cursor-pointer"
                    >
                      Voir
                    </router-link>
                    <!-- :disabled="Date.now() - new Date(item.creation_datetime).getTime() > 3600000 * 24" -->
                  </div>
                  <div v-if="apiType === 'invoices' && !hideDelete">
                    <!-- page === Math.ceil(count / limit) && index === items.length - 1" -->
                    <UIButtonDocRemove
                      v-if="page === 1 && index === 0"
                      :apiType="apiType"
                      :doc_number="item.doc_number"
                      :numberPrefix="numberPrefix"
                      @removed="items = items.filter((_, innerIndex) => innerIndex !== index)"
                      class="ml-2 -mr-2 mt-1"
                    />
                  </div>
                  <button
                    v-if="apiType === 'quotes' && !isInvoiced(item)"
                    @click="toInvoice(item, false)"
                    class="ml-auto block text-indigo-600 hover:text-indigo-900 cursor-pointer mt-1"
                  >
                    Générer facture
                  </button>
                  <router-link
                    v-if="apiType === 'quotes' && !isInvoiced(item)"
                    class="ml-auto block text-indigo-600 hover:text-indigo-900 cursor-pointer mt-2"
                    :to="`/acomptes/quote/${item.doc_number}`"
                  >
                    Générer facture acompte
                  </router-link>
                  <span v-if="apiType === 'quotes' && isInvoiced(item)"> Prix déjà facturer </span>
                  <!-- <pre class="text-left">{{ { a: item.linked_invoices_price, b: item.price_incl } }}</pre> -->
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <UIAtomPagination v-model="page" :offset="offset" :limit="limit" :count="count" />
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import Multiselect from '@vueform/multiselect'

import useApi from '@/hooks/useApi'
import TableProduction from '@/components/Table/Production'
import ProductionStatus from '@/components/UI/Atom/ProductionStatus'
import UIAtomPagination from '@/components/UI/Atom/Pagination'
import UIButtonDocRemove from '@/components/UI/Atom/ButtonDocRemove'
import PRODUCTION_STATUS from '@/constants/PRODUCTION_STATUS'
import PRODUCTION_STAGES from '@/constants/PRODUCTION_STAGES'
import PAYMENT_STATUS from '@/constants/PAYMENT_STATUS'

export default {
  props: {
    type: {
      type: String,
      required: true,
      default: 'Facture',
    },
    apiType: {
      type: String,
      required: true,
    },
    apiParams: {
      type: Object,
      default() {
        return {}
      },
    },
    numberPrefix: {
      type: String,
      required: true,
      default: 'FA',
    },
    paymentStatus: Boolean,
    addPayment: Boolean,
    hideDelete: Boolean,
  },
  components: {
    Multiselect,
    TableProduction,
    ProductionStatus,
    UIAtomPagination,
    UIButtonDocRemove,
  },
  emits: ['pay'],
  setup(props) {
    const api = useApi()
    const router = useRouter()
    const route = useRoute()

    const isInvoice = props.apiType === 'invoices'
    const isProduction = props.apiType === 'production'
    const isCreditNotes = props.apiType === 'credit-notes' || props.apiType === 'creditNotes'

    const filters = ref({
      status_production: isProduction ? ['WAITING_RESPONSE', 'IN_PRODUCTION'] : [],
      stage_production: isProduction ? [] : [],
      status_payments: [],
    })

    const isInvoiced = item =>
      item.linked_invoices_price > 0 &&
      item.price_incl > 0 &&
      Math.abs(item.price_incl) - Math.abs(item.linked_invoices_price) <= 0.01

    const page = ref(route.query.page > 0 ? Number(route.query.page) : 1)
    const count = ref(-1)
    const limit = ref(10)
    const items = ref([])

    const offset = computed(() => limit.value * (page.value - 1))
    const refresh = () => {
      api[props.apiType]
        .list({
          limit: limit.value,
          offset: offset.value,
          status_production: !isCreditNotes ? filters.value.status_production : undefined,
          stage_production: isProduction ? filters.value.stage_production : undefined,
          status_payments: isInvoice ? filters.value.status_payments : undefined,
          ...props.apiParams,
        })
        .then(({ data }) => {
          if (data.count || data.count === 0 || count.value === -1) {
            count.value = data.count || 0
          }
          if (Array.isArray(data.list)) {
            items.value = data.list
          }
        })
    }
    refresh()
    watch(() => page.value, refresh)
    watch(filters.value, () => {
      page.value = 1
      refresh()
    })

    const hasProduction = computed(() => ['quotes', 'invoices'].includes(props.apiType))
    const createProduction = item => {
      if (!hasProduction.value) return
      api[props.apiType]
        .toProduction({
          doc_number: item.doc_number,
          type: item.type,
        })
        .then(res => {
          item.status = 'IN_PRODUCTION'
          item.creation_datetime = new Date()
          item.production_id = res.data?.production_id || null
          item.updated_datetime = new Date()
        })
    }

    const toInvoice = item => {
      const suffix = [item.name, item.vat].filter(el => el).join(' - ')
      if (confirm(`Etes-vous sûr de vouloir générer une facture depuis le devis DV-${item.doc_number} (${suffix}) ?`)) {
        api[props.apiType]
          .toInvoice({
            doc_number: item.doc_number,
          })
          .then(() => {
            router.push('/invoices')
          })
      }
    }

    const columns = [props.type, 'Prix HTVA/TVAC', 'Client']
    if (hasProduction.value) {
      columns.push('Production')
    }
    if (props.paymentStatus) {
      columns.push('Status')
    }
    columns.push('')
    return {
      columns,
      page,
      limit,
      offset,
      count,
      items,
      hasProduction,
      filters,
      PRODUCTION_STATUS,
      PRODUCTION_STAGES,
      PAYMENT_STATUS,
      createProduction,
      toInvoice,
      isInvoiced,
    }
  },
}
</script>

<style></style>
